import React from 'react';
import { Eye, Edit, Printer, IndianRupee, Calendar, CreditCard, User, Phone, MoreVertical, FileText, Activity, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useBillingUtils } from '@/hooks/useBillingUtils';
// Import centralized types
import type { Invoice } from '@/types';

interface InvoiceStatusBadgeProps {
  status: 'Paid' | 'Pending' | 'Partial';
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function InvoiceStatusBadge({ status, showIcon = true, size = 'md' }: InvoiceStatusBadgeProps) {
  const { getStatusConfig } = useBillingUtils();
  const statusConfig = getStatusConfig(status);
  
  return (
    <Badge 
      className={cn(
        "flex items-center gap-1 font-medium border",
        size === 'sm' ? 'text-xs px-2 py-1' : size === 'lg' ? 'text-sm px-3 py-1.5' : 'text-xs px-2 py-1',
        statusConfig.className
      )}
      style={{
        backgroundColor: statusConfig.bgColor,
        color: statusConfig.textColor,
        borderColor: statusConfig.borderColor
      }}
    >
      {showIcon && statusConfig.icon}
      <span>{statusConfig.label}</span>
    </Badge>
  );
}

interface InvoiceAmountDisplayProps {
  totalAmount: number;
  paidAmount?: number;
  balanceAmount?: number;
  paymentMethod: string;
  layout?: 'compact' | 'detailed';
}

export function InvoiceAmountDisplay({ 
  totalAmount, 
  paidAmount = 0, 
  balanceAmount = 0, 
  paymentMethod, 
  layout = 'detailed' 
}: InvoiceAmountDisplayProps) {
  const { formatCurrency, getPaymentMethodIcon } = useBillingUtils();

  if (layout === 'compact') {
    return (
      <div className="text-right">
        <div className="font-bold text-base text-foreground">
          {formatCurrency(totalAmount)}
        </div>
        {balanceAmount > 0 && (
          <div className="text-xs text-warning font-medium">
            Due: {formatCurrency(balanceAmount)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="text-right space-y-1">
      <div className="text-xl font-bold text-foreground">
        {formatCurrency(totalAmount)}
      </div>
      {balanceAmount > 0 && (
        <div className="text-sm text-warning font-medium">
          Due: {formatCurrency(balanceAmount)}
        </div>
      )}
      {paidAmount > 0 && balanceAmount > 0 && (
        <div className="text-xs text-muted-foreground">
          Paid: {formatCurrency(paidAmount)}
        </div>
      )}
      <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
        {getPaymentMethodIcon(paymentMethod)}
        {paymentMethod}
      </div>
    </div>
  );
}

interface InvoiceActionButtonsProps {
  invoice: Invoice;
  layout?: 'horizontal' | 'vertical';
  variant?: 'patient' | 'clinic';
  onView: (invoice: Invoice) => void;
  onEdit: (invoice: Invoice) => void;
  onPrint: (invoice: Invoice) => void;
  onPayment?: (invoice: Invoice) => void;
  onViewPatient?: (patientId: string) => void;
  onMoreActions?: (invoice: Invoice) => void;
}

export function InvoiceActionButtons({
  invoice,
  layout = 'horizontal',
  variant = 'patient',
  onView,
  onEdit,
  onPrint,
  onPayment,
  onViewPatient,
  onMoreActions
}: InvoiceActionButtonsProps) {
  const containerClass = layout === 'vertical' ? 'flex flex-col gap-2' : 'flex gap-1.5';
  const buttonSize = layout === 'vertical' ? 'sm' : 'sm';

  const viewButton = (
    <Button 
      variant="outline" 
      size={buttonSize}
      onClick={() => onView(invoice)}
      className="flex items-center gap-1"
    >
      <Eye className="w-3 h-3" />
      View
    </Button>
  );

  const editButton = (
    <Button 
      variant="outline" 
      size={buttonSize}
      onClick={() => onEdit(invoice)}
      className="flex items-center gap-1 border-primary/20 hover:bg-primary/5 text-primary"
    >
      <Edit className="w-3 h-3" />
      Edit
    </Button>
  );

  const printButton = (
    <Button 
      variant="outline" 
      size={buttonSize}
      onClick={() => onPrint(invoice)}
      className="flex items-center gap-1"
    >
      <Printer className="w-3 h-3" />
      {layout === 'horizontal' ? 'Print' : <span className="sr-only">Print</span>}
    </Button>
  );

  const paymentButton = invoice.balance_amount > 0 && onPayment && (
    <Button 
      size={buttonSize}
      onClick={() => onPayment(invoice)}
      className={cn(
        "flex items-center gap-1 bg-success hover:bg-success/80 text-success-foreground",
        layout === 'vertical' ? 'w-full' : ''
      )}
    >
      <IndianRupee className="w-3 h-3" />
      {variant === 'patient' ? 'Pay' : 'Record Payment'}
    </Button>
  );

  if (layout === 'vertical') {
    return (
      <div className={containerClass}>
        <div className="flex gap-2">
          {viewButton}
          {editButton}
          {printButton}
        </div>
        {paymentButton}
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {viewButton}
      {editButton}
      {printButton}
      {paymentButton}
      {onMoreActions && (
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onMoreActions(invoice)}>
          <MoreVertical className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}

interface InvoiceCardProps {
  invoice: Invoice;
  variant: 'patient' | 'clinic';
  onView: (invoice: Invoice) => void;
  onEdit: (invoice: Invoice) => void;
  onPrint: (invoice: Invoice) => void;
  onPayment?: (invoice: Invoice) => void;
  onViewPatient?: (patientId: string) => void;
  className?: string;
}

export function InvoiceCard({
  invoice,
  variant,
  onView,
  onEdit,
  onPrint,
  onPayment,
  onViewPatient,
  className
}: InvoiceCardProps) {
  const { formatDate, formatCurrency } = useBillingUtils();

  // Get payment status styling with overdue detection
  const getPaymentStatusStyling = (status: string, invoiceDate: string) => {
    const isOverdue = status === 'Pending' && 
      new Date(invoiceDate) < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // 30 days overdue
    
    if (isOverdue) {
      return {
        borderClass: 'border-l-destructive',
        bgClass: 'bg-destructive/5',
        statusIcon: <AlertCircle className="w-4 h-4 text-destructive animate-pulse" />,
        statusColor: 'text-destructive',
        isOverdue: true
      };
    }
    
    switch (status) {
      case 'Paid':
        return {
          borderClass: 'border-l-success',
          bgClass: 'bg-success/5 hover:bg-success/10',
          statusIcon: <CheckCircle className="w-4 h-4 text-success" />,
          statusColor: 'text-success',
          isOverdue: false
        };
      case 'Pending':
        return {
          borderClass: 'border-l-warning',
          bgClass: 'bg-warning/5 hover:bg-warning/10',
          statusIcon: <AlertCircle className="w-4 h-4 text-warning" />,
          statusColor: 'text-warning',
          isOverdue: false
        };
      case 'Partial':
        return {
          borderClass: 'border-l-primary',
          bgClass: 'bg-primary/5 hover:bg-primary/10',
          statusIcon: <Activity className="w-4 h-4 text-primary" />,
          statusColor: 'text-primary',
          isOverdue: false
        };
      default:
        return {
          borderClass: 'border-l-muted',
          bgClass: 'bg-muted/5',
          statusIcon: <FileText className="w-4 h-4 text-muted-foreground" />,
          statusColor: 'text-muted-foreground',
          isOverdue: false
        };
    }
  };

  const statusStyling = getPaymentStatusStyling(invoice.payment_status, invoice.date);

  if (variant === 'patient') {
    return (
      <div className={cn(
        "p-4 hover:bg-muted/30 transition-colors border-l-4",
        statusStyling.borderClass,
        statusStyling.bgClass,
        className
      )}>
        {/* Single Row: All key info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <div className="font-semibold text-foreground">{invoice.invoice_number}</div>
              <div className="text-sm text-muted-foreground">{formatDate(invoice.date)}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className={cn("text-lg font-bold", statusStyling.statusColor)}>
                {formatCurrency(invoice.total_amount)}
              </div>
              {invoice.payment_status === 'Paid' ? (
                <div className="text-sm text-success">Paid</div>
              ) : (
                <div className="text-sm text-warning">Due: {formatCurrency(invoice.balance_amount)}</div>
              )}
            </div>
            
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => onView(invoice)} className="h-8 w-8 p-0">
                <Eye className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEdit(invoice)} className="h-8 w-8 p-0">
                <Edit className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onPrint(invoice)} className="h-8 w-8 p-0">
                <Printer className="w-4 h-4" />
              </Button>
              {invoice.balance_amount > 0 && onPayment && (
                <Button size="sm" onClick={() => onPayment(invoice)} className="h-8 px-3 text-xs bg-success hover:bg-success/80 text-success-foreground">
                  Pay
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Clinic variant - Simple design
  return (
    <Card className={cn(
      "medical-card hover:shadow-md transition-shadow border-l-4",
      statusStyling.borderClass,
      statusStyling.bgClass,
      className
    )}>
      <CardContent className="p-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <div className="font-semibold text-foreground">{invoice.patient_name}</div>
              <div className="text-sm text-muted-foreground">
                {invoice.invoice_number} • {formatDate(invoice.date)}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className={cn("text-lg font-bold", statusStyling.statusColor)}>
                {formatCurrency(invoice.total_amount)}
              </div>
              {invoice.payment_status === 'Paid' ? (
                <div className="text-sm text-success">Paid</div>
              ) : (
                <div className="text-sm text-warning">Due: {formatCurrency(invoice.balance_amount)}</div>
              )}
            </div>
            
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => onView(invoice)} className="h-8 w-8 p-0">
                <Eye className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEdit(invoice)} className="h-8 w-8 p-0">
                <Edit className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onPrint(invoice)} className="h-8 w-8 p-0">
                <Printer className="w-4 h-4" />
              </Button>
              {invoice.balance_amount > 0 && onPayment && (
                <Button size="sm" onClick={() => onPayment(invoice)} className="h-8 px-3 text-xs bg-success hover:bg-success/80 text-success-foreground">
                  Pay
                </Button>
              )}
              {onViewPatient && (
                <Button variant="ghost" size="sm" onClick={() => onViewPatient(invoice.patient_id!)} className="h-8 w-8 p-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 